// Fiche d'une réclamation : description, demandeur, objet lié, pièces, historique ; affectation à
// un service ou à un agent (habilité si sensible), marquage sensible motivé, réponse visible par
// l'usager, clôture après réponse, évaluation de l'usager.
import { Bandeau, Lien, LienBouton, Panneau, PuceEtat, tailleLisible } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useParams } from 'react-router';
import { useUtilisateurs } from '../../api/requetes';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useServices } from '../demarches/contenu';
import { ActionMetier } from '../metier/ActionMetier';
import { nomUtilisateur } from '../administration/PageUtilisateurs';
import { LIBELLES_ETAT_RECLAMATION, LIBELLES_EVENEMENT_RECLAMATION, LIBELLES_OBJET_LIE, useReclamation } from './donnees';
import { puceReclamation } from './PageReclamations';
import commun from '../../commun/commun.module.css';
import styles from './reclamations.module.css';

export function PageReclamation() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const fiche = useReclamation(id);
  const services = useServices();
  const utilisateurs = useUtilisateurs();
  const r = fiche.data;
  const invalider = [['reclamations'], ['indicateurs']];
  const domaine = r?.sensible ? 'reclamation_sensible' : 'reclamation';
  const peutTraiter = peut(contexte, domaine, 'modifier') || peut(contexte, domaine, 'valider');
  const peutCloturer = peutTraiter || peut(contexte, domaine, 'decider');
  const ouverte = r ? r.etat === 'recue' || r.etat === 'en_instruction' : false;
  const agents = (utilisateurs.data ?? []).filter((u) => u.statut_appartenance === 'active').map((u) => ({ valeur: u.appartenance_id, libelle: nomUtilisateur(u) }));
  const nomAgent = (appartenanceId: string | null) => {
    if (!appartenanceId) return 'non affectée à un agent';
    const u = utilisateurs.data?.find((x) => x.appartenance_id === appartenanceId);
    return u ? nomUtilisateur(u) : `appartenance ${appartenanceId.slice(0, 8)}`;
  };

  return (
    <>
      <EnTetePage
        etiquette={r ? `Réclamation, ${r.categorie_libelle}` : 'Réclamation'}
        titre={r ? `Réclamation ${r.numero}` : 'Réclamation'}
        description={r ? <span className={commun.ligne}>{puceReclamation(r)}{r.sensible ? <PuceEtat ton="retard" libelle="Sensible, accès restreint aux personnes habilitées" /> : null}</span> : null}
        actions={<LienBouton href="/reclamations" icone={ArrowLeft}>Réclamations</LienBouton>}
      />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L1-A" fonction="La fiche de la réclamation">
        {r ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              <Panneau titre="Réclamation">
                <p className={styles.description}>{r.description}</p>
                <dl className={commun.definitions}>
                  <dt>Demandeur</dt>
                  <dd>{r.demandeur.nom ?? 'non renseigné'}</dd>
                  <dt>Reçue le</dt>
                  <dd>{dateHeure(r.cree_le)}</dd>
                  <dt>Échéance de réponse</dt>
                  <dd>
                    {date(r.echeance)}
                    {r.en_retard ? <strong className={styles.retard}> , dépassée</strong> : null}
                  </dd>
                  <dt>Objet lié</dt>
                  <dd>{r.objet_lie ? r.objet_lie.type === 'dossier' ? <Lien href={`/dossiers/${r.objet_lie.id}`}>Dossier lié</Lien> : `${LIBELLES_OBJET_LIE[r.objet_lie.type] ?? r.objet_lie.type} ${r.objet_lie.id.slice(0, 8)}` : 'aucun'}</dd>
                  <dt>Service</dt>
                  <dd>{r.service?.nom ?? 'non affectée'}</dd>
                  <dt>Agent</dt>
                  <dd>{nomAgent(r.affectee_appartenance_id)}</dd>
                  {r.sensible ? (
                    <>
                      <dt>Sensible</dt>
                      <dd>{r.sensible_motif ? `Marquée par un agent : ${r.sensible_motif}` : 'Déclarée sensible par l\'usager ou par sa catégorie'}</dd>
                    </>
                  ) : null}
                </dl>
              </Panneau>
              <Panneau titre="Pièces">
                {r.pieces.length === 0 ? <p className={commun.na}>Aucune pièce jointe.</p> : null}
                <ul className={styles.pieces}>
                  {r.pieces.map((p) => (
                    <li key={p.id}>
                      <a className={commun.lienSimple} href={`/api/v1/reclamations/${r.id}/pieces/${p.id}`}>
                        {p.titre}
                      </a>{' '}
                      <span className={commun.na}>
                        {p.type_mime}, {tailleLisible(p.taille)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panneau>
              <Panneau titre="Réponse à l'usager" interne={false}>
                {r.reponse ? (
                  <>
                    <p className={styles.description}>{r.reponse}</p>
                    <p className={commun.na}>Répondue le {dateHeure(r.repondue_le)} ; visible par l&apos;usager dans son suivi et notifiée.</p>
                  </>
                ) : (
                  <p className={commun.na}>Aucune réponse pour l&apos;instant.</p>
                )}
                {r.evaluation ? (
                  <p>
                    Évaluation de l&apos;usager : <strong>{r.evaluation.note} sur 5</strong>
                    {r.evaluation.commentaire ? ` : ${r.evaluation.commentaire}` : ''}
                    {r.evaluation.le ? ` (${date(r.evaluation.le)})` : ''}
                  </p>
                ) : null}
              </Panneau>
            </div>
            <div className={commun.pile}>
              <Panneau titre="Traitement">
                {!peutTraiter ? <p className={commun.na}>Vous pouvez consulter cette réclamation ; son traitement est réservé aux agents du service ou aux responsables.</p> : null}
                <div className={styles.actions}>
                  {ouverte && peutTraiter ? (
                    <ActionMetier
                      libelle="Affecter"
                      titre="Affecter la réclamation"
                      explication={r.sensible ? 'Réclamation sensible : l\'agent désigné doit disposer d\'une habilitation nominative validée.' : 'À un service, à un agent, ou aux deux. La réclamation passe en instruction.'}
                      chemin={`/reclamations/${r.id}/affecter`}
                      champs={[
                        { nom: 'service_id', libelle: 'Service', options: (services.data ?? []).map((s) => ({ valeur: s.id, libelle: s.nom })) },
                        agents.length > 0 ? { nom: 'appartenance_id', libelle: 'Agent', options: agents } : { nom: 'appartenance_id', libelle: 'Identifiant d\'appartenance de l\'agent', aide: 'La liste des agents n\'est pas lisible avec vos droits.' },
                      ]}
                      succes="Réclamation affectée."
                      invalider={invalider}
                      variante="secondaire"
                    />
                  ) : null}
                  {!r.sensible && ouverte && peutTraiter ? (
                    <ActionMetier libelle="Marquer sensible" titre="Marquer la réclamation sensible" explication="Elle ne sera plus visible que des personnes habilitées ; ses pièces deviennent confidentielles ; un agent affecté non habilité est retiré." chemin={`/reclamations/${r.id}/marquer-sensible`} champs={[{ nom: 'motif', libelle: 'Motif', obligatoire: true, multiligne: true }]} succes="Réclamation marquée sensible." invalider={invalider} variante="secondaire" />
                  ) : null}
                  {ouverte && peutTraiter ? (
                    <ActionMetier libelle="Répondre" titre="Répondre à l'usager" explication="La réponse est visible par l'usager dans son suivi et lui est notifiée ; il pourra l'évaluer de 1 à 5." chemin={`/reclamations/${r.id}/repondre`} champs={[{ nom: 'reponse', libelle: 'Réponse', obligatoire: true, multiligne: true }]} succes="Réponse envoyée à l'usager." invalider={invalider} variante="principal" />
                  ) : null}
                  {r.etat === 'repondue' && peutCloturer ? (
                    <ActionMetier libelle="Clôturer" titre="Clôturer la réclamation" explication="La réclamation est close ; l'usager peut encore l'évaluer." chemin={`/reclamations/${r.id}/cloturer`} champs={[{ nom: 'motif', libelle: 'Motif', multiligne: true }]} succes="Réclamation clôturée." invalider={invalider} variante="principal" />
                  ) : null}
                </div>
                {r.sensible && !peut(contexte, 'reclamation_sensible', 'voir') ? <Bandeau type="attention">Accès restreint : cette réclamation est sensible.</Bandeau> : null}
              </Panneau>
              <Panneau titre="Historique">
                <ol className={styles.historique}>
                  {r.historique.map((h, i) => (
                    <li key={i}>
                      <strong>{LIBELLES_EVENEMENT_RECLAMATION[h.type] ?? h.type}</strong>
                      <span className={commun.na}>
                        {' '}
                        {dateHeure(h.horodatage)} ; état : {LIBELLES_ETAT_RECLAMATION[h.etat_apres] ?? h.etat_apres}
                        {typeof h.donnees.note === 'number' ? `, note ${String(h.donnees.note)}` : ''}
                        {typeof h.donnees.motif === 'string' && h.donnees.motif ? `, motif : ${h.donnees.motif}` : ''}
                      </span>
                    </li>
                  ))}
                </ol>
              </Panneau>
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
